/* global globalThis */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../../../api-client/AxiosClient";
import Spinner from "../../../common/Spinner/Spinner";
import { toast } from "react-toastify";

import {
  processJsonData,
  globalFilterFn,
  calculateTableHeight,
  handleColumnResizeStart as resizeColumn,
} from "./TableUtils";
import "./HistoryData.css";
import StickyNotesPopup from "./StickyNotesPopup";

// Import new components
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import TablePagination from "./TablePagination";
import SidePanels from "./SidePanels";

const getFilteredData = (data, showInscopeOnly, showMandatoryOnly) => {
  let filteredData = [...data];

  if (showInscopeOnly) {
    filteredData = filteredData.filter(
      (row) => row.inscope === "Y" || row["In-scope"] === "Y",
    );
  }

  if (showMandatoryOnly) {
    filteredData = filteredData.filter(
      (row) =>
        row.mandatoryfeild === "Y" || row["Mandatory Field (S4)"] === "Y",
    );
  }

  return filteredData;
};

const CreateTable = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { STTMapping } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}",
  );
  const { canUpdate } = STTMapping;
  const [showInscopeOnly, setShowInscopeOnly] = useState(false);
  const [showMandatoryOnly, setShowMandatoryOnly] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [dependentValues, setDependentValues] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });
  const [localValue, setLocalValue] = useState({});
  const [loading, setLoading] = useState(false);
  const [dropdownValue, setDropdownValue] = useState(null);
  const [uploadedFileList, setUploadedFileList] = useState([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [showAIAgent, setShowAIAgent] = useState(false);
  const [panelWidth, setPanelWidth] = useState(800);
  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const [showReverseEngineering, setShowReverseEngineering] = useState(false);
  const [activeTab, setActiveTab] = useState("mapping"); // 'mapping' or 'transformation'
  const [selectedTargetStructure, setSelectedTargetStructure] = useState(null);
  const [refreshKey] = useState(0);

  const [stickyNotePopup, setStickyNotePopup] = useState({
    isOpen: false,
    value: "",
    rowIndex: null,
    columnId: null,
  });

  const filteredData = useMemo(() => {
    return getFilteredData(data, showInscopeOnly, showMandatoryOnly);
  }, [data, showInscopeOnly, showMandatoryOnly]);

  const panelRef = useRef(null);
  const resizerRef = useRef(null);

  const uploadedFileID = useRef(location.state?.fileID || null);

  const [pageData, setPageData] = useState({
    fileID: location.state?.fileID || null,
  });

  const [sourceTableColumnName, setSourceTableColumnName] = useState(null);
  const [fieldTypeMap, setFieldTypeMap] = useState(new Map());
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnSizes, setColumnSizes] = useState({});
  const [columnVisibilityMenu, setColumnVisibilityMenu] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState({
    usermappingfiledetailsid: false,
    usermappingfileid: false,
    templateid: false,
    isactive: false,
    mapping_file_details_id: false,
    mapping_file_id: false,
  });

  const [hideColumnsData, setHideColumnsData] = useState([]);
  const [resizingRow, setResizingRow] = useState(null);
  const [rowSizes, setRowSizes] = useState({});
  const [rowPinning, setRowPinning] = useState({ top: [] });
  const [rowSelection, setRowSelection] = useState({});
  const [frozenColumns, setFrozenColumns] = useState(new Set());
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);

  const containerRef = useRef(null);
  const visibilityButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchAllFiles = async (configId) => {
    if (!configId) {
      setUploadedFileList([]);
      setData([]);
      setColumns([]);
      setDropdownValue(null);
      return;
    }

    setLoading(true);

    try {
      const filesResponse = await axiosClient.get(
        `/api/Mapper/GetMapperFilesbyOrgConfig/${configId}`,
      );

      const formattedFiles = filesResponse.data.map((item) => ({
        value: item.userMappingFileId,
        label: item.uploadedObjDescription,
        id: item?.corporationId,
      }));

      setUploadedFileList(formattedFiles);

      if (uploadedFileID.current) {
        const selectedFile = formattedFiles.find(
          (opt) => opt.value === uploadedFileID.current,
        );

        if (selectedFile) {
          setDropdownValue(selectedFile);
        } else {
          setDropdownValue(
            formattedFiles.length > 0 ? formattedFiles[0] : null,
          );
        }
      } else if (formattedFiles.length > 0) {
        setDropdownValue(formattedFiles[0]);
      }
    } catch (error) {
      console.error("Failed to fetch file list:", error);
      toast.error("Failed to fetch file list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const storedConfigId = localStorage.getItem("organization_config_id");
      if (storedConfigId) {
        setSelectedConfiguration(storedConfigId);
        const storedFileData = sessionStorage.getItem("selectedFileData");
        if (storedFileData) {
          const parsedFileData = JSON.parse(storedFileData);
          setPageData((prev) => ({ ...prev, fileID: parsedFileData.fileID }));
        }
      }
    } catch (error) {
      console.error("Failed to load organization config:", error);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "organization_config_id") {
        try {
          if (e.newValue === null) {
            setSelectedConfiguration(null);
            setUploadedFileList([]);
            setData([]);
            setColumns([]);
            setDropdownValue(null);
            return;
          }

          setSelectedConfiguration(e.newValue);

          if (e.newValue) {
            setData([]);
            setColumns([]);
            setDropdownValue(null);
            fetchAllFiles(e.newValue);
          }
        } catch (error) {
          console.error("Invalid organization config data:", error);
        }
      }
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "organization_config_id") {
        try {
          if (e.detail.newValue === null || e.detail.newValue === undefined) {
            setSelectedConfiguration(null);
            setUploadedFileList([]);
            setData([]);
            setColumns([]);
            setDropdownValue(null);
            return;
          }

          setSelectedConfiguration(e.detail.newValue);

          if (e.detail.newValue) {
            setData([]);
            setColumns([]);
            setDropdownValue(null);
            fetchAllFiles(e.detail.newValue);
          }
        } catch (error) {
          console.error("Invalid organization config data:", error);
        }
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    globalThis.addEventListener(
      "customStorageChange",
      handleCustomStorageChange,
    );

    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      globalThis.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange,
      );
    };
  }, []);

  useEffect(() => {
    if (selectedConfiguration) {
      fetchAllFiles(selectedConfiguration);
    } else {
      setUploadedFileList([]);
      setData([]);
      setColumns([]);
      setDropdownValue(null);
    }
  }, [selectedConfiguration]);

  useEffect(() => {
    const handleGetFilters = async () => {
      const organizationConfigId = localStorage.getItem(
        "organization_config_id",
      );
      const url = organizationConfigId
        ? `/api/Mapper/GetFilters?organizationConfigId=${organizationConfigId}`
        : "/api/Mapper/GetFilters";
      
      try {
        const response = await axiosClient.get(url);
        
        if (!response?.data) {
          console.warn("No filter data received from API");
          return;
        }

        setHideColumnsData([...response.data.hideColumnsData]);
        setShowInscopeOnly(response.data.showInscopeOnly);
        setShowMandatoryOnly(response.data.showMandatoryOnly);
        if (response.data.globalFilter) {
          setGlobalFilter(response.data.globalFilter);
        }
        setDropdownValue(response.data.dropdownValue);
        setColumnVisibility(response.data.columnVisibility);
      } catch (error) {
        console.error("Error loading filter preferences:", error);
        toast.error("Failed to load filter preferences. Using defaults.");
      }
    };

    handleGetFilters();
  }, []);

  useEffect(() => {
    const payload = {
      hideColumnsData,
      showInscopeOnly,
      showMandatoryOnly,
      globalFilter,
      dropdownValue: dropdownValue || {},
      columnVisibility,
    };

    const timer = setTimeout(() => {
      const handleApiSubmit = async () => {
        try {
          const organizationConfigId = localStorage.getItem(
            "organization_config_id",
          );
          const url = organizationConfigId
            ? `/api/Mapper/SaveFilters?organizationConfigId=${organizationConfigId}`
            : "/api/Mapper/SaveFilters";
          await axiosClient.post(url, payload);
        } catch (error) {
          console.error("Error saving filter preferences:", error);
          const errorMsg = error.response?.data?.message || "Failed to save filter preferences";
          toast.warning(errorMsg + ". Your preferences may not persist.");
        }
      };

      handleApiSubmit();
    }, 500);
    return () => clearTimeout(timer);
  }, [
    hideColumnsData,
    showInscopeOnly,
    showMandatoryOnly,
    globalFilter,
    dropdownValue,
    columnVisibility,
  ]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingPanel) return;

      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const newWidth = containerRect.right - e.clientX;

        const minWidth = 300;
        const maxWidth = Math.floor(containerRect.width * 0.7);

        const constrainedWidth = Math.max(
          minWidth,
          Math.min(newWidth, maxWidth),
        );
        setPanelWidth(constrainedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingPanel(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    if (isResizingPanel) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingPanel]);

  const getColumnIdsByCategory = (category) =>
    table
      .getAllLeafColumns()
      .filter(
        (col) =>
          col.columnDef?.columncategoryname?.toLowerCase() ===
          category.toLowerCase(),
      )
      .map((col) => col.id);

  const updateVisibilityForColumnIds = (columnIds, shouldBeVisible) => {
    setColumnVisibility((old) => {
      const updated = { ...old };
      columnIds.forEach((id) => {
        updated[id] = shouldBeVisible;
      });
      return updated;
    });
  };

  // Sync hideColumnsData and columnVisibility
  // When user toggles a hide column, update both hideColumnsData and columnVisibility
  const handleHideColumnsData = (columnToHide) => {
    setHideColumnsData((prev) => {
      const isUnchecking = prev.includes(columnToHide);
      const newHideColumns = isUnchecking
        ? prev.filter((col) => col !== columnToHide)
        : [...prev, columnToHide];
      const shouldBeVisible = isUnchecking;
      const affectedColumnIds = getColumnIdsByCategory(columnToHide);
      updateVisibilityForColumnIds(affectedColumnIds, shouldBeVisible);
      return newHideColumns;
    });
  };

  const isGroupVisible = (groupKey) => {
    const groupColumnIds = getColumnIdsByCategory(groupKey);
    return groupColumnIds.some((id) => {
      const isVisible = columnVisibility[id] !== false;
      return isVisible;
    });
  };

  const checkGroupVisibility = (group) => {
    const groupKey = group.toLowerCase();
    if (isGroupVisible(groupKey) && hideColumnsData.includes(groupKey)) {
      setHideColumnsData((prev) => prev.filter((col) => col !== groupKey));
    }
  };

  useEffect(() => {
    typeOfColumns.forEach(checkGroupVisibility);
  }, [columnVisibility]);

  const updateCheckboxValue = async (row, columnId, checked) => {
    try {
      setLoading(true);
      const value = checked ? "Y" : "N";
      const updatedRow = { ...row.original, [columnId]: value };

      const detailsId =
        updatedRow.mapping_file_details_id ||
        updatedRow.usermappingfiledetailsid ||
        updatedRow.UserMappingFileDetailsId;

      let updateRequest = {
        UserMappingFileDetailsId: detailsId,
        inscope: updatedRow.inscope || updatedRow["In-scope"] || "N",
        mandatoryfeild:
          updatedRow.mandatoryfeild ||
          updatedRow["Mandatory Field (S4)"] ||
          "N",
        mandatoryfield:
          updatedRow.mandatoryfeild ||
          updatedRow["Mandatory Field (S4)"] ||
          "N",
        comments: String(updatedRow.Comments ?? updatedRow.comments ?? ""),
        datacleansingconsiderations: String(
          updatedRow["Data Cleansing Considerations"] ??
            updatedRow.datacleansingconsiderations ??
            "",
        ),
        comments: String(updatedRow["Comments"] ?? updatedRow.comments ?? ""),
        sourcefield: String(
          updatedRow["Source field (1-to-1 mapping)"] ??
            updatedRow.sourcefield ??
            "",
        ),
        crossreferencetable: String(
          updatedRow["Cross Reference Table"] ??
            updatedRow.crossreferencetable ??
            "",
        ),
        reusablelogic: String(
          updatedRow["Reusable Logic (Y/N)"] ?? updatedRow.reusablelogic ?? "",
        ),
        criticalerrorhandling: String(
          updatedRow["Critical Error Handeling (if applicable)"] ??
            updatedRow.criticalerrorhandling ??
            "",
        ),
        technicalcomments: String(
          updatedRow["Technical Comments"] ??
            updatedRow.technicalcomments ??
            "",
        ),
      };

      if (!updateRequest.UserMappingFileDetailsId) {
        console.error(
          "Missing UserMappingFileDetailsId. Available keys:",
          Object.keys(updatedRow),
        );
        toast.error("Cannot update: Missing record ID");
        throw new Error("UserMappingFileDetailsId is required");
      }

      fieldTypeMap.forEach((fieldConfig, excelColumnName) => {
        if (
          fieldConfig.dbColumnName &&
          updatedRow[excelColumnName] !== undefined
        ) {
          let value = updatedRow[excelColumnName];

          if (fieldConfig.editType === "checkbox") {
            value = value === true || value === "Y" ? "Y" : "N";
          } else {
            value = value === null || value === undefined ? "" : String(value);
          }

          updateRequest[fieldConfig.dbColumnName] = value;
        }
      });

      if (updateRequest.decimalscale !== undefined) {
        const decimalValue = updateRequest.decimalscale;
        updateRequest.decimalscale =
          decimalValue === "" || decimalValue === null
            ? 0
            : Number.parseInt(decimalValue, 10) || 0;
      }

      setData((prevData) => {
        const newData = [...prevData];
        const actualIndex = newData.findIndex((item) => {
          const itemID = item.mapping_file_details_id || item.usermappingfiledetailsid || item.UserMappingFileDetailsId;
          const rowID = row.original.mapping_file_details_id || row.original.usermappingfiledetailsid || row.original.UserMappingFileDetailsId;
          return itemID && rowID && itemID === rowID;
        });
        if (actualIndex !== -1) newData[actualIndex] = updatedRow;
        return newData;
      });

      const payload = {
        userMappingFileId: pageData.fileID,
        updateMapperDetailsRequests: [updateRequest],
      };

      console.log(
        "Sending checkbox update payload:",
        JSON.stringify(payload, null, 2),
      );

      const response = await axiosClient.post(
        "/api/Mapper/UpdateMapperFile",
        payload,
      );

      if (response.status === 200) {
        toast.success("Checkbox updated successfully");
      }
    } catch (error) {
      console.error("Checkbox update failed:", error);
      setData((prevData) => {
        const newData = [...prevData];
        newData[row.index] = row.original;
        return newData;
      });
      toast.error("Failed to update checkbox value");
    } finally {
      setLoading(false);
    }
  };

  const handleColumnResizeStart = (e, header) => {
    resizeColumn(e, header, setResizingColumn, columnSizes, setColumnSizes);
  };



useEffect(() => {
  if (!data.length || !sourceTableColumnName) return;

  const uniqueTableNames = [
    ...new Set(
      data
        .map((row) => row[sourceTableColumnName])
        .filter((name) => name && name.trim() !== "")
    ),
  ];

  uniqueTableNames.forEach((tableName) => {
    if (!dependentValues[tableName]) {
      fetchDependentValues(tableName);
    }
  });
}, [data, sourceTableColumnName, dropdownOptions]);

  const handleRowResizeStart = (e, rowId) => {
    e.preventDefault();
    setResizingRow(rowId);

    const startY = e.pageY;
    const startHeight = rowSizes[rowId] || 36;

    const handleMouseMove = (e) => {
      const newHeight = Math.max(startHeight + (e.pageY - startY), 24);
      setRowSizes((prev) => ({ ...prev, [rowId]: newHeight }));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setResizingRow(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const toggleColumnFreeze = (columnId) => {
    setFrozenColumns((prev) => {
      const newFrozen = new Set(prev);
      if (newFrozen.has(columnId)) {
        newFrozen.delete(columnId);
      } else {
        newFrozen.add(columnId);
      }
      return newFrozen;
    });

    setTimeout(() => {
      setData((prevData) => [...prevData]);
    }, 0);
  };

  const areAllColumnsSelected = () => {
    const hiddenSystemColumns = new Set([
      "usermappingfiledetailsid",
      "usermappingfileid",
      "templateid",
      "isactive",
    ]);

    return table
      .getAllLeafColumns()
      .filter((column) => !hiddenSystemColumns.has(column.id))
      .every((column) => column.getIsVisible());
  };

useEffect(() => {
  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
}, [showInscopeOnly, showMandatoryOnly, globalFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      globalFilterFn, // ← registers it so TanStack can find it by name
    },
    globalFilterFn: "globalFilterFn", // ← reference by registered key
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination,
      columnVisibility,
      rowPinning,
      rowSelection,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowPinningChange: setRowPinning,
    onRowSelectionChange: setRowSelection,
    autoResetPageIndex: false,
    defaultColumn: { size: 120 },
    enableRowPinning: true,
    enableRowSelection: true,
  });

  const toggleColumnVisibilityMenu = () => {
    setColumnVisibilityMenu(!columnVisibilityMenu);
  };

  const selectAllColumns = () => {
    const hiddenSystemColumns = new Set([
      "usermappingfiledetailsid",
      "usermappingfileid",
      "templateid",
      "isactive",
    ]);

    const newVisibility = {};
    table.getAllLeafColumns().forEach((column) => {
      newVisibility[column.id] = !hiddenSystemColumns.has(column.id);
    });

    setColumnVisibility(newVisibility);
    setHideColumnsData([]);
  };

  const unselectAllColumns = () => {
    const newVisibility = {};
    table.getAllLeafColumns().forEach((column) => {
      newVisibility[column.id] = false;
    });

    setColumnVisibility(newVisibility);
    setHideColumnsData(typeOfColumns.map((col) => col.toLowerCase()));
  };



useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      visibilityButtonRef.current &&
      !visibilityButtonRef.current.contains(event.target)
    ) {
      setColumnVisibilityMenu(false);
    }
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowGroupDropdown(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  const CSV_COLUMNS_TO_EXCLUDE = new Set([
    "mapping_file_id",
    "mapping_file_details_id",
    "isactive",
  ]);

  const convertToCSV = (arr) => {
    if (!arr || arr.length === 0) return "";
    const keys = Object.keys(arr[0]).filter(
      (k) => !CSV_COLUMNS_TO_EXCLUDE.has(k.toLowerCase()),
    );
    const csvRows = [
      keys.join(","),
      ...arr.map((row) =>
        keys
          .map((k) => `"${(row[k] ?? "").toString().replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    return csvRows.join("\r\n");
  };

  const downloadCSV = (csv, filename = "mapping_data.csv") => {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    globalThis.URL.revokeObjectURL(url);
  };

  const normalizeCheckboxValue = (value) => {
    if (!value) return "N";
    const validValues = ["Y", "y", "Yes", "TRUE", "true", 1, "1"];
    return validValues.includes(value) ? "Y" : "N";
  };

  const processData = (data) => {
    return data.map((row) => ({
      ...row,
      "In-scope": normalizeCheckboxValue(row["In-scope"]),
      "Mandatory Field (S4)": normalizeCheckboxValue(
        row["Mandatory Field (S4)"],
      ),
    }));
  };

  const fetchMappingDataByExcelID = async (
    fileID,
    exportCSV = false,
    silent = false,
  ) => {
    try {
      setLoading(true);

      if (!silent) {
        setPagination({ pageIndex: 0, pageSize: 15 });
      }

      const [tablesResponse, templateResponse, mapperResponse] =
        await Promise.all([
          axiosClient.get("/api/Mapper/GetAvailbleTables", {
            params: { UserMappingFileId: fileID },
          }),
          axiosClient.get(
            "/api/Mapper/GetTemplateDetailsByUploadFile?userMappingFileId=" +
              fileID,
          ),
          axiosClient.get(
            "/api/Mapper/GetMapperFileDetails?userMappingFileId=" + fileID,
          ),
        ]);

      const normalizedData = processData(mapperResponse.data);

      if (exportCSV) {
        const csv = convertToCSV(normalizedData);
        downloadCSV(csv, `mapping_data_${fileID}.csv`);
        setLoading(false);
        return;
      }
      processJsonData({
        jsonData: normalizedData,
        dropDownData: tablesResponse.data,
        resTemplateData: templateResponse.data,
        setColumns,
        setData,
        setFieldTypeMap,
        setDropdownOptions,
        setSourceTableColumnName,
        updateRowFn: updateRow,
      });
    } catch (error) {
      // Check if it's a 400 validation error for missing mapping file ID
      if (
        error.response?.status === 400 &&
        error.response?.data?.errors?.UserMappingFileId?.some((msg) =>
          msg.includes("mapping_file_id is required"),
        )
      ) {
        toast.warning("Please select mapping Document for mapping");
      } else {
        toast.error(
          "Error fetching mapping data: " + (error.message || "Unknown error"),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dropdownValue) {
      setPageData((prev) => ({ ...prev, fileID: dropdownValue.value }));
      uploadedFileID.current = null;
      fetchMappingDataByExcelID(dropdownValue.value);

      sessionStorage.setItem("selectedFileId", dropdownValue.value);
      sessionStorage.setItem("selectedFileName", dropdownValue.label);

      sessionStorage.setItem(
        "selectedFileData",
        JSON.stringify({
          fileID: dropdownValue.value,
          fileName: dropdownValue.label,
          corporationId: dropdownValue.id,
        }),
      );

      globalThis.dispatchEvent(
        new CustomEvent("customStorageChange", {
          detail: {
            key: "selectedFileId",
            newValue: dropdownValue.value,
          },
        }),
      );
    }
  }, [dropdownValue]);

  const updateRow = async (row, columnHeader, updatedValue, fieldName) => {
    const normalizedValue = updatedValue === undefined ? "" : updatedValue;
    if (row.original[columnHeader] === updatedValue) return;
    let updatedRow = { ...row.original, [columnHeader]: normalizedValue };

    if (fieldName === "STN") {
      await fetchDependentValues(updatedValue);
      fieldTypeMap.forEach((fieldConfig) => {
        if (
          fieldConfig.dbColumnName === "sourcefieldname" ||
          fieldConfig.dbColumnName === "sourcedatatype" ||
          fieldConfig.dbColumnName === "sourcedatalength" // ADD THIS
        ) {
          updatedRow[fieldConfig.excelColumnName] = "";
        }
      });
    } else if (fieldName === "SFN") {
      // If cleared, wipe dependent fields
      if (!updatedValue) {
        fieldTypeMap.forEach((fieldConfig) => {
          if (
            fieldConfig.dbColumnName === "sourcedatatype" ||
            fieldConfig.dbColumnName === "sourcedatalength"
          ) {
            updatedRow[fieldConfig.excelColumnName] = "";
          }
        });
      } else {
        const selectedColumn = dependentValues[
          row.original[sourceTableColumnName]
        ]?.find((col) => col.columnName === updatedValue);

        if (selectedColumn) {
          fieldTypeMap.forEach((fieldConfig) => {
            if (fieldConfig.dbColumnName === "sourcedatatype") {
              updatedRow[fieldConfig.excelColumnName] =
                selectedColumn.columnDataType;
            }

            if (fieldConfig.dbColumnName === "sourcedatalength") {
              let dataLength = "";
              if (selectedColumn.columnDataCharLength) {
                dataLength = selectedColumn.columnDataCharLength;
              } else if (selectedColumn.columnPrecision) {
                dataLength = selectedColumn.columnPrecision;
              }
              updatedRow[fieldConfig.excelColumnName] = dataLength;
            }
          });
        }
      }
    }

    setData((prevData) => {
      const newData = [...prevData];
      const actualIndex = newData.findIndex((item) => {
        const itemID = item.mapping_file_details_id || item.usermappingfiledetailsid || item.UserMappingFileDetailsId;
        const rowID = row.original.mapping_file_details_id || row.original.usermappingfiledetailsid || row.original.UserMappingFileDetailsId;
        return itemID && rowID && itemID === rowID;
      });
      if (actualIndex !== -1) newData[actualIndex] = updatedRow;
      return newData;
    });

    try {
      await updateData(updatedRow);

      setLocalValue((prev) => {
        const updated = { ...prev };
        delete updated[`${row.index}-${columnHeader}`];
        return updated;
      });
    } catch (error) {
      console.error("Update failed:", error);
      setData((prevData) => {
        const newData = [...prevData];
         const actualIndex = newData.findIndex((item) => {
          const itemID = item.mapping_file_details_id || item.usermappingfiledetailsid || item.UserMappingFileDetailsId;
          const rowID = row.original.mapping_file_details_id || row.original.usermappingfiledetailsid || row.original.UserMappingFileDetailsId;
          return itemID && rowID && itemID === rowID;
        });
        if (actualIndex !== -1) newData[actualIndex] = row.original;
        return newData;
      });
      toast.error("Update failed");
    }
  };

  const updateData = async (rowData) => {
    try {
      setLoading(true);

      const detailsId =
        rowData.mapping_file_details_id ||
        rowData.usermappingfiledetailsid ||
        rowData.UserMappingFileDetailsId;

      const updateRequest = {
        UserMappingFileDetailsId: detailsId,
        inscope: rowData.inscope || rowData["In-scope"] || "N",
        mandatoryfeild:
          rowData.mandatoryfeild || rowData["Mandatory Field (S4)"] || "N",
        mandatoryfield:
          rowData.mandatoryfeild || rowData["Mandatory Field (S4)"] || "N",
        comments: String(rowData["Comments"] ?? rowData.comments ?? ""),
        datacleansingconsiderations: String(
          rowData["Data Cleansing Considerations"] ??
            rowData.datacleansingconsiderations ??
            "",
        ),
        sourcefield: String(
          rowData["Source field (1-to-1 mapping)"] ?? rowData.sourcefield ?? "",
        ),
        crossreferencetable: String(
          rowData["Cross Reference Table"] ?? rowData.crossreferencetable ?? "",
        ),
        reusablelogic: String(
          rowData["Reusable Logic (Y/N)"] ?? rowData.reusablelogic ?? "",
        ),
        criticalerrorhandling: String(
          rowData["Critical Error Handeling (if applicable)"] ??
            rowData.criticalerrorhandling ??
            "",
        ),
        technicalcomments: String(
          rowData["Technical Comments"] ?? rowData.technicalcomments ?? "",
        ),
      };

      if (!updateRequest.UserMappingFileDetailsId) {
        console.error(
          "Missing UserMappingFileDetailsId. Available keys:",
          Object.keys(rowData),
        );
        toast.error("Cannot update: Missing record ID");
        throw new Error("UserMappingFileDetailsId is required");
      }

      if (rowData["Source - Field Description"] !== undefined) {
        updateRequest.sourcefielddescription = String(
          rowData["Source - Field Description"] ?? "",
        );
      }
      if (rowData["View - Field Name"] !== undefined) {
        updateRequest.viewfieldname = String(
          rowData["View - Field Name"] ?? "",
        );
      }

      fieldTypeMap.forEach((fieldConfig, excelColumnName) => {
        if (
          fieldConfig.dbColumnName &&
          rowData[excelColumnName] !== undefined
        ) {
          let value = rowData[excelColumnName];

          if (fieldConfig.editType === "checkbox") {
            value = value === true || value === "Y" ? "Y" : "N";
          } else {
            value = value === null || value === undefined ? "" : String(value);
          }

          updateRequest[fieldConfig.dbColumnName] = value;
        }
      });

      if (updateRequest.decimalscale !== undefined) {
        const decimalValue = updateRequest.decimalscale;
        updateRequest.decimalscale =
          decimalValue === "" || decimalValue === null
            ? 0
            : Number.parseInt(decimalValue, 10) || 0;
      }

      if (updateRequest.length !== undefined) {
        const lengthValue = updateRequest.length;
        if (typeof lengthValue === "string" && lengthValue.trim() !== "") {
          updateRequest.length = lengthValue;
        }
      }

      console.log(
        "Sending update payload:",
        JSON.stringify(
          {
            userMappingFileId: pageData.fileID,
            updateMapperDetailsRequests: [updateRequest],
          },
          null,
          2,
        ),
      );

      const response = await axiosClient.post("/api/Mapper/UpdateMapperFile", {
        userMappingFileId: pageData.fileID,
        updateMapperDetailsRequests: [updateRequest],
      });

      if (response.status === 200) {
        toast.success("Updated successfully");
      }
    } catch (error) {
      console.error("Update failed:", error);
      console.error("Error response:", error.response?.data);
      toast.error(
        "Failed to update: " +
          (error.response?.data?.title || error.message || "Unknown error"),
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchDependentValues = async (value) => {
    if (!value || value.trim() === "") {
      return;
    }

    if (dependentValues[value] && dependentValues[value].length > 0) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosClient.get(
        "/api/Mapper/GetAvailbleTableColumns",
        {
          params: { TableName: value, UserMappingFileId: pageData.fileID },
        },
      );
      setDependentValues((prev) => ({ ...prev, [value]: response.data }));
    } catch (error) {
      console.error("Failed to fetch dependent data:", error);
      toast.error("Failed to fetch dependent data");
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPagination((prev) => {
      const newPageIndex = Math.floor(
        (prev.pageIndex * prev.pageSize) / newSize,
      );
      return { pageSize: newSize, pageIndex: newPageIndex };
    });
    table.setPageSize(newSize);
  };

  const customSelectStyles = {
    control: (provided) => ({ ...provided, minHeight: "28px", height: "28px" }),
    valueContainer: (provided) => ({
      ...provided,
      height: "28px",
      padding: "0 6px",
    }),
    input: (provided) => ({ ...provided, margin: "0px" }),
    indicatorsContainer: (provided) => ({ ...provided, height: "28px" }),
    dropdownIndicator: (provided) => ({ ...provided, padding: "4px" }),
    clearIndicator: (provided) => ({ ...provided, padding: "4px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const handleTextInputChange = (rowIndex, columnId, value) => {
    setLocalValue((prev) => ({
      ...prev,
      [`${rowIndex}-${columnId}`]: value,
    }));
  };

  const getFrozenColumnLeftPosition = (columnId) => {
    const headerGroups = table.getHeaderGroups();
    let position = 0;

    if (headerGroups.length > 0) {
      const firstHeaderGroup = headerGroups[0];
      for (const header of firstHeaderGroup.headers) {
        if (header.id === columnId) break;
        if (frozenColumns.has(header.id)) {
          position += columnSizes[header.id] || header.column.getSize();
        }
      }
    }
    return position;
  };

  const getFrozenColumnStyle = (
    columnId,
    isHeader = false,
    isPinnedRow = false,
  ) => {
    const isFrozen = frozenColumns.has(columnId);

    const getZIndex = () => {
      if (isFrozen) {
        if (isHeader) return 15;
        return isPinnedRow ? 12 : 10;
      }
      if (isHeader) return 5;
      return isPinnedRow ? 3 : 1;
    };

    const getBackgroundColor = () => {
      if (isFrozen) {
        if (isHeader) return "#e8f4f8";
        return isPinnedRow ? "#e3f2fd" : "#f8fcff";
      }
      if (isHeader) return "#f4f4f4";
      return isPinnedRow ? "#e3f2fd" : "inherit";
    };

    const baseStyle = {
      position: isFrozen || isHeader ? "sticky" : "relative",
      left: isFrozen ? `${getFrozenColumnLeftPosition(columnId)}px` : "auto",
      top: isHeader ? "0" : "auto",
      zIndex: getZIndex(),
      backgroundColor: getBackgroundColor(),
      borderRight: isFrozen ? "2px solid #2196f3" : "1px solid #dee2e6",
      boxShadow: isFrozen ? "2px 0 5px rgba(0,0,0,0.1)" : "none",
    };
    return baseStyle;
  };

  const typeOfColumns = ["Source", "Target", "Transformation"];

  return (
    <div
      className="d-flex"
      style={{
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "row",
      }}
      ref={containerRef}
    >
      {/* Main Table Section */}
      <div
        className="d-flex flex-column"
        style={{
          flex: 1,
          minWidth: 0,
          transition:
            "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          width:
            showAIAgent || showReverseEngineering
              ? `calc(100% - ${panelWidth}px)`
              : "100%",
          maxWidth:
            showAIAgent || showReverseEngineering
              ? `calc(100% - ${panelWidth}px)`
              : "100%",
        }}
      >
        {loading && <Spinner />}
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0, overflow: "hidden" }}
        >
          {/* Table Header Component */}
          <TableHeader
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            dropdownValue={dropdownValue}
            setDropdownValue={setDropdownValue}
            uploadedFileList={uploadedFileList}
            customSelectStyles={customSelectStyles}
            columnVisibilityMenu={columnVisibilityMenu}
            toggleColumnVisibilityMenu={toggleColumnVisibilityMenu}
            visibilityButtonRef={visibilityButtonRef}
            table={table}
            areAllColumnsSelected={areAllColumnsSelected}
            selectAllColumns={selectAllColumns}
            unselectAllColumns={unselectAllColumns}
            showGroupDropdown={showGroupDropdown}
            setShowGroupDropdown={setShowGroupDropdown}
            dropdownRef={dropdownRef}
            typeOfColumns={typeOfColumns}
            hideColumnsData={hideColumnsData}
            handleHideColumnsData={handleHideColumnsData}
            showInscopeOnly={showInscopeOnly}
            setShowInscopeOnly={setShowInscopeOnly}
            showMandatoryOnly={showMandatoryOnly}
            setShowMandatoryOnly={setShowMandatoryOnly}
            showAIAgent={showAIAgent}
            setShowAIAgent={setShowAIAgent}
            setShowReverseEngineering={setShowReverseEngineering}
            showReverseEngineering={showReverseEngineering}
            navigate={navigate}
            pageData={pageData}
            fetchMappingDataByExcelID={fetchMappingDataByExcelID}
          />

          {/* Table Container */}
          {data.length > 0 && (
            <div
              key={refreshKey}
              style={{
                overflow: "auto",
                width: "100%",

                maxWidth: "100%",
                height: calculateTableHeight(pagination),
                flex: "1 1 auto",
                border: "1px solid #dee2e6",
                position: "relative",
              }}
            >
              <table
                style={{
                  width: "max-content",
                  borderCollapse: "collapse",
                  border: "1px solid #dee2e6",
                  position: "relative",
                }}
              >
                {/* Table Header */}
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#f4f4f4",
                    zIndex: 50,
                    boxShadow: "0 2px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      style={{
                        background: "#f4f4f4",
                        borderBottom: "1px solid #dee2e6",
                        height: "36px",
                      }}
                    >
                      {headerGroup.headers.map((header) => {
                        const columnDef = header.column.columnDef;
                        if (
                          hideColumnsData.includes(
                            columnDef?.columncategoryname?.toLowerCase(),
                          )
                        ) {
                          return null;
                        }
                        return (
                          <th
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            style={{
                              ...getFrozenColumnStyle(header.id, true, false),
                              padding: "2px 4px",
                              border: "1px solid #dee2e6",
                              cursor: "pointer",
                              position: frozenColumns.has(header.id)
                                ? "sticky"
                                : "relative",
                              left: frozenColumns.has(header.id)
                                ? `${getFrozenColumnLeftPosition(header.id)}px`
                                : undefined,
                              zIndex: frozenColumns.has(header.id) ? 15 : 5,
                              backgroundColor: frozenColumns.has(header.id)
                                ? "#e8f4f8"
                                : "#f4f4f4",
                              width:
                                columnSizes[header.id] ||
                                header.column.getSize(),
                              minWidth: "50px",
                              fontSize: "12px",
                              fontWeight: "600",
                              textAlign: "left",
                              height: "36px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              boxShadow: frozenColumns.has(header.id)
                                ? "2px 0 5px rgba(0,0,0,0.1)"
                                : "none",
                              borderRight: frozenColumns.has(header.id)
                                ? "2px solid #2196f3"
                                : "1px solid #dee2e6",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleColumnFreeze(header.id);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleColumnFreeze(header.id);
                                  }
                                }}
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  backgroundColor: frozenColumns.has(header.id)
                                    ? "#2196f3"
                                    : "#ccc",
                                  color: "white",
                                  fontSize: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  borderRadius: "2px",
                                  zIndex: 10,
                                  border: "none",
                                  padding: 0,
                                }}
                                title={
                                  frozenColumns.has(header.id)
                                    ? "Unfreeze column"
                                    : "Freeze column"
                                }
                                aria-label={
                                  frozenColumns.has(header.id)
                                    ? "Unfreeze column"
                                    : "Freeze column"
                                }
                              >
                                📌
                              </button>
                              <span>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                                {(() => {
                                  const sortDirection =
                                    header.column.getIsSorted();
                                  if (sortDirection === "asc") return " 🔼";
                                  if (sortDirection === "desc") return " 🔽";
                                  return "";
                                })()}
                              </span>
                            </span>
                            {/* Resizer */}
                            <button
                              type="button"
                              aria-label="Resize column"
                              onMouseDown={(e) =>
                                handleColumnResizeStart(e, header)
                              }
                              style={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                height: "100%",
                                width: "5px",
                                background:
                                  resizingColumn === header.id
                                    ? "#2196f3"
                                    : "transparent",
                                cursor: "col-resize",
                                userSelect: "none",
                                touchAction: "none",
                                border: "none",
                                padding: 0,
                              }}
                            />
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                {/* Table Body Component */}
                <TableBody
                  table={table}
                  hideColumnsData={hideColumnsData}
                  columnSizes={columnSizes}
                  rowSizes={rowSizes}
                  resizingColumn={resizingColumn}
                  resizingRow={resizingRow}
                  frozenColumns={frozenColumns}
                  canUpdate={canUpdate}
                  localValue={localValue}
                  dependentValues={dependentValues}
                  sourceTableColumnName={sourceTableColumnName}
                  dropdownOptions={dropdownOptions}
                  handleColumnResizeStart={handleColumnResizeStart}
                  handleRowResizeStart={handleRowResizeStart}
                  toggleColumnFreeze={toggleColumnFreeze}
                  updateCheckboxValue={updateCheckboxValue}
                  handleTextInputChange={handleTextInputChange}
                  updateRow={updateRow}
                  setStickyNotePopup={setStickyNotePopup}
                  getFrozenColumnStyle={getFrozenColumnStyle}
                  getFrozenColumnLeftPosition={getFrozenColumnLeftPosition}
                />
              </table>
            </div>
          )}

          {/* Pagination Component */}
          {data.length > 0 && (
            <TablePagination
              table={table}
              pagination={pagination}
              handlePageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      {/* Side Panels Component */}
      <SidePanels
        showAIAgent={showAIAgent}
        setShowAIAgent={setShowAIAgent}
        showReverseEngineering={showReverseEngineering}
        setShowReverseEngineering={setShowReverseEngineering}
        panelWidth={panelWidth}
        setPanelWidth={setPanelWidth}
        isResizingPanel={isResizingPanel}
        setIsResizingPanel={setIsResizingPanel}
        containerRef={containerRef}
        panelRef={panelRef}
        resizerRef={resizerRef}
        pageData={pageData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedTargetStructure={selectedTargetStructure}
        setSelectedTargetStructure={setSelectedTargetStructure}
        dropdownValue={dropdownValue}
        onRefreshTable={async () => {
          const [tablesResponse, templateResponse, mapperResponse] =
            await Promise.all([
              axiosClient.get("/api/Mapper/GetAvailbleTables", {
                params: { UserMappingFileId: pageData.fileID },
              }),
              axiosClient.get(
                "/api/Mapper/GetTemplateDetailsByUploadFile?userMappingFileId=" +
                  pageData.fileID,
              ),
              axiosClient.get(
                "/api/Mapper/GetMapperFileDetails?userMappingFileId=" +
                  pageData.fileID,
              ),
            ]);
          const normalizedData = processData(mapperResponse.data);
          processJsonData({
            jsonData: normalizedData,
            dropDownData: tablesResponse.data,
            resTemplateData: templateResponse.data,
            setColumns,
            setData,
            setFieldTypeMap,
            setDropdownOptions,
            setSourceTableColumnName,
            updateRowFn: updateRow,
          });
        }}
      />

      {/* Sticky Notes Popup */}
      <StickyNotesPopup
        isOpen={stickyNotePopup.isOpen}
        onClose={() =>
          setStickyNotePopup({
            isOpen: false,
            value: "",
            rowIndex: null,
            columnId: null,
            position: null,
          })
        }
        onSave={(value) => {
          if (stickyNotePopup.rowIndex !== null && stickyNotePopup.columnId) {
            const row = table.getRowModel().rows[stickyNotePopup.rowIndex];
            if (row) {
              updateRow(row, stickyNotePopup.columnId, value);
            }
          }
        }}
        initialValue={stickyNotePopup.value}
        position={stickyNotePopup.position}
      />
    </div>
  );
};

export default CreateTable;
